import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../hooks/useSupabase';
import { startCheckout, openBillingPortal, isPremium, fetchPrices, formatPrice, FREE_RECIPE_LIMIT, FREE_HISTORY_DAYS } from '../lib/billing';
import { isOnline } from '../lib/supabase';
import { trackEvent } from '../lib/analytics';
import Confetti from './Confetti';

const BENEFITS = [
  { emoji: '📚', text: 'Unlimited custom recipes', free: `${FREE_RECIPE_LIMIT} on Free` },
  { emoji: '📅', text: 'Full meal-plan history', free: `${FREE_HISTORY_DAYS} days on Free` },
  { emoji: '🔗', text: 'Import recipes from any website', free: null },
  { emoji: '📸', text: 'Photos on your recipes', free: null },
];

// Webhook usually lands within seconds of checkout; poll a little longer
// before falling back to a "still processing" note.
const SUCCESS_POLL_MS = 3000;
const SUCCESS_POLL_LIMIT = 15;

export default function UpgradeScreen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { householdId, currentMember } = useAuth();
  const { subscription, loading: subLoading, refetch } = useSubscription(householdId);
  const premium = isPremium(subscription);
  const isAdult = currentMember?.type === 'adult';

  const [prices, setPrices] = useState(null);
  const [plan, setPlan] = useState('annual');
  const [redirecting, setRedirecting] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  const checkoutResult = searchParams.get('checkout'); // 'success' | 'cancelled' | null
  const [pollCount, setPollCount] = useState(0);
  const pollRef = useRef(null);

  useEffect(() => {
    fetchPrices().then(setPrices);
  }, []);

  // After returning from a successful checkout, poll until the webhook has
  // flipped the household to Premium so the unlock feels immediate.
  useEffect(() => {
    if (checkoutResult !== 'success' || premium) {
      if (pollRef.current) clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(() => {
      refetch();
      setPollCount((c) => c + 1);
    }, SUCCESS_POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [checkoutResult, premium, refetch]);

  useEffect(() => {
    if (pollCount >= SUCCESS_POLL_LIMIT && pollRef.current) clearInterval(pollRef.current);
  }, [pollCount]);

  const handleUpgrade = async () => {
    if (!isOnline()) { setCheckoutError('You must be online to upgrade.'); return; }
    setRedirecting(true);
    setCheckoutError(null);
    trackEvent('Checkout Started', { plan });
    const { error } = await startCheckout(plan);
    // On success the browser navigates away — only failures reach here
    if (error) {
      setCheckoutError(error);
      setRedirecting(false);
      refetch();
    }
  };

  const handleManage = async () => {
    setRedirecting(true);
    const { error } = await openBillingPortal();
    if (error) {
      setCheckoutError(error);
      setRedirecting(false);
    }
  };

  const priceFor = (key) => (prices && !prices.error ? formatPrice(prices[key]) : null);
  const savingsPct = (() => {
    const m = prices?.monthly?.amount;
    const a = prices?.annual?.amount;
    if (!m || !a) return null;
    const pct = Math.round((1 - a / (m * 12)) * 100);
    return pct > 0 ? pct : null;
  })();

  const card = 'bg-panel rounded-2xl p-5 lg:p-6 border border-hairline';

  // ── Post-purchase success ─────────────────────────────────────────────────
  if (checkoutResult === 'success') {
    return (
      <div className="relative max-w-md mx-auto space-y-4 text-center pt-8">
        {premium && <Confetti count={56} />}
        <span className="text-6xl block">{premium ? '🏆' : '⏳'}</span>
        <h2 className="font-display text-2xl font-semibold text-ink">
          {premium ? "You're Premium!" : 'Finishing up...'}
        </h2>
        <p className="font-body text-sm text-ink-soft">
          {premium
            ? 'Your whole household is unlocked. Enjoy!'
            : pollCount >= SUCCESS_POLL_LIMIT
              ? "Payment received. Your upgrade is processing and will unlock shortly. You don't need to do anything."
              : 'Confirming your subscription with Stripe...'}
        </p>
        {premium ? (
          <button
            onClick={() => { setSearchParams({}); navigate('/'); }}
            className="w-full py-3 rounded-2xl bg-lime text-canvas font-body text-sm font-bold hover:bg-lime transition-colors"
          >
            Start cooking
          </button>
        ) : (
          <div className="flex justify-center">
            <svg className="w-6 h-6 animate-spin text-ink-muted" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        )}
      </div>
    );
  }

  // ── Already premium ───────────────────────────────────────────────────────
  if (!subLoading && premium) {
    return (
      <div className="max-w-md mx-auto space-y-4 text-center pt-8">
        <span className="text-6xl block">🏆</span>
        <h2 className="font-display text-2xl font-semibold text-ink">You're Premium!</h2>
        <p className="font-body text-sm text-ink-soft">
          Your household has {subscription?.plan === 'annual' ? 'the annual' : 'the monthly'} plan.
        </p>
        {isAdult && (
          <button
            onClick={handleManage}
            disabled={redirecting}
            className="w-full py-3 rounded-2xl bg-surface text-ink-soft font-body text-sm font-semibold hover:bg-surface transition-colors disabled:opacity-50"
          >
            {redirecting ? 'Opening...' : 'Manage Subscription'}
          </button>
        )}
        {checkoutError && <p className="font-body text-xs text-red-600">{checkoutError}</p>}
      </div>
    );
  }

  // ── Upsell ────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-md mx-auto space-y-4">
      {checkoutResult === 'cancelled' && (
        <div className={`${card} bg-surface`}>
          <p className="font-body text-sm text-ink-soft">
            Checkout cancelled. You haven't been charged.
          </p>
        </div>
      )}

      <div className="text-center pt-2">
        <span className="text-5xl block mb-2">🏆</span>
        <h2 className="font-display text-2xl font-semibold text-ink">Suppertide Premium</h2>
        <p className="font-body text-sm text-ink-soft mt-1">
          One upgrade unlocks your whole household
        </p>
      </div>

      <div className={`${card} space-y-3`}>
        {BENEFITS.map((b) => (
          <div key={b.text} className="flex items-center gap-3">
            <span className="text-xl w-8 text-center">{b.emoji}</span>
            <div className="flex-1">
              <p className="font-body text-sm font-medium text-ink">{b.text}</p>
              {b.free && <p className="font-body text-[11px] text-ink-muted">{b.free}</p>}
            </div>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4 text-mint shrink-0">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
        ))}
      </div>

      {isAdult ? (
        <>
          <div className="grid grid-cols-2 gap-3">
            {[
              { key: 'monthly', label: 'Monthly', suffix: '/mo' },
              { key: 'annual', label: 'Annual', suffix: '/yr' },
            ].map(({ key, label, suffix }) => (
              <button
                key={key}
                type="button"
                onClick={() => setPlan(key)}
                className={`relative rounded-2xl p-4 border-2 text-left transition-colors ${
                  plan === key ? 'border-lime bg-lime/5' : 'border-hairline bg-panel'
                }`}
              >
                {key === 'annual' && savingsPct && (
                  <span className="absolute -top-2 right-3 px-2 py-0.5 rounded-full bg-mint text-canvas font-body text-[10px] font-bold">
                    Save {savingsPct}%
                  </span>
                )}
                <p className="font-body text-xs font-medium text-ink-soft">{label}</p>
                <p className="font-display text-lg font-semibold text-ink">
                  {priceFor(key) ? (
                    <>{priceFor(key)}<span className="font-body text-xs font-medium text-ink-muted">{suffix}</span></>
                  ) : (
                    <span className="inline-block w-16 h-5 rounded bg-surface animate-pulse" />
                  )}
                </p>
              </button>
            ))}
          </div>

          <button
            onClick={handleUpgrade}
            disabled={redirecting || subLoading}
            className="w-full py-3 rounded-2xl bg-lime text-canvas font-body text-sm font-bold hover:bg-lime transition-colors disabled:opacity-50"
          >
            {redirecting ? 'Heading to checkout...' : 'Upgrade Household'}
          </button>
          {checkoutError && <p className="font-body text-xs text-red-600 text-center">{checkoutError}</p>}
          <p className="font-body text-[11px] text-ink-muted text-center">
            Secure payment via Stripe. Cancel anytime.
          </p>
        </>
      ) : (
        <div className={`${card} text-center`}>
          <span className="text-2xl block mb-1">🧑‍🍳</span>
          <p className="font-body text-sm font-medium text-ink">
            Ask an adult in your household to upgrade
          </p>
          <p className="font-body text-xs text-ink-muted mt-1">
            Only adults can manage the household subscription
          </p>
        </div>
      )}
    </div>
  );
}
